import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Paper,
    Typography,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    LinearProgress,
    Tabs,
    Tab,
    TextField,
    Avatar,
    Button,
    Divider,
    IconButton,
    Tooltip,
    Menu, Stack,
    MenuItem, Grid, TableHead, TableContainer, Snackbar, Alert,
    Modal, Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select
} from '@mui/material';
import {
    Description, Flag, CheckCircle, StackedBarChart, Person, People, CheckCircle as CheckCircleIcon,
    Business, Close as CloseIcon,
    Subject as SubjectIcon,
    Person as PersonIcon,
    PlaylistAddCheck as PlaylistAddCheckIcon,
    Timeline as TimelineIcon,
    CalendarToday as CalendarTodayIcon,
    Comment as CommentIcon,
    Add as AddIcon,
    Visibility as VisibilityIcon,
    AccessTime as AccessTimeIcon,
    AttachFile as AttachFileIcon,
    Description as DescriptionIcon,
    Lock as LockIcon,
    Delete as DeleteIcon,
    TaskAlt, Subject, Comment,
    ArrowDropDown, AccountCircle, Edit as EditIcon, AccessAlarm, AccessTimeFilled,
    PriorityHigh
} from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { styled } from '@mui/material/styles';
import Back from '../../../assets/Back.png';
import moment from 'moment';
import CreateTask from './CreateTask';

const statusColors = {
    'Ready for Dev': 'primary',
    'In Development': 'secondary',
    'Completed': 'success',
    'On Hold': 'default',
    'default': 'default'
};

const statusTextColors = {
    'Ready for Dev': '#ffffff',
    'In Development': '#ffffff',
    'Completed': '#ffffff',
    'On Hold': '#757575',
    'default': '#757575',
};

const priorityColors = {
    'High': 'error',
    'Medium': 'warning',
    'Low': 'success',
    'default': 'default',
};

const priorityTextColors = {
    'High': '#ffffff',
    'Medium': '#ffffff',
    'Low': '#ffffff',
    'default': '#757575',
};

const workTypes = ['Development', 'Enhancement', 'Bug', 'Feature', 'Testing', 'Documentation'];

const stripedRowColors = {
    odd: '#f0f0f0',
    even: '#fff',
};

const StripedTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '& *': {
        padding: theme.spacing(1),
    },
}));

const formatTime = (hoursDecimal) => {
    if (hoursDecimal === null || hoursDecimal === undefined || hoursDecimal === '') {
        return '--:--';
    }

    // Convert decimal hours to total minutes
    const totalMinutes = hoursDecimal * 60;
    let hours = Math.floor(totalMinutes / 60);
    let minutes = Math.round(totalMinutes % 60);

    // Handle case where minutes round up to 60
    if (minutes === 60) {
        hours += 1;
        minutes = 0;
    }

    return `${hours} hrs ${minutes.toString().padStart(2, '0')} mins`;
}

// Dummy data for user story details
const dummyUserStoryDetails = {
    story_id: 'US-001',
    story_title: 'Location Validation for Delivery Zones',
    description: 'As a delivery manager, I want to validate delivery locations against defined zones so that I can ensure efficient delivery routing.',
    epic_name: 'Share-Ur-Truck App',
    epic_color: '#2196f3',
    product_owner: 'Venku Naidu Velle',
    team_lead: 'Viyyapu Naveen Kumar',
    start_date: '2024-11-01',
    due_date: '2024-12-15',
    estimated_hours: 80,
    actual_hours: 56,
    progress: 71,
    status: 'Ready for Dev',
    priority: 'Medium',
    total_tasks: 5,
    completed_tasks: 3,
    attachments: [
        {
            file_url: '#',
            file_name: 'user_story_specs.pdf',
        },
        {
            file_url: '#',
            file_name: 'zone_mapping.png',
        }
    ],
    assignees: [
        { first_name: 'John', last_name: 'Doe', initials: 'JD', color: '#ff5722' },
        { first_name: 'Mike', last_name: 'Parker', initials: 'MP', color: '#4caf50' },
        { first_name: 'Alice', last_name: 'Lee', initials: 'AL', color: '#2196f3' }
    ],
    comments: [
        {
            id: 1,
            comment: 'Initial requirements gathered from stakeholders',
            user_type: 'Product Owner',
            user: { first_name: 'Venku', last_name: 'Naidu' },
            createdAt: '2024-10-28T10:30:00'
        },
        {
            id: 2,
            comment: 'Technical feasibility analysis completed',
            user_type: 'Team Lead',
            user: { first_name: 'Viyyapu', last_name: 'Naveen' },
            createdAt: '2024-10-30T14:20:00'
        }
    ],
    tasks: [
        {
            task_id: 'TASK-001',
            task_title: 'Define delivery zone boundaries',
            status: 'Completed',
            assignees: [{ first_name: 'John', last_name: 'Doe', initials: 'JD' }],
            estimated_hours: 8,
            actual_hours: 6,
            work_type: 'Analysis'
        },
        {
            task_id: 'TASK-002',
            task_title: 'Implement geofencing logic',
            status: 'In Progress',
            assignees: [{ first_name: 'Mike', last_name: 'Parker', initials: 'MP' }],
            estimated_hours: 16,
            actual_hours: 10,
            work_type: 'Development'
        },
        {
            task_id: 'TASK-003',
            task_title: 'Create validation API endpoints',
            status: 'Completed',
            assignees: [{ first_name: 'Alice', last_name: 'Lee', initials: 'AL' }],
            estimated_hours: 12,
            actual_hours: 8,
            work_type: 'Development'
        },
        {
            task_id: 'TASK-004',
            task_title: 'Test with real location data',
            status: 'To Do',
            assignees: [{ first_name: 'John', last_name: 'Doe', initials: 'JD' }],
            estimated_hours: 8,
            actual_hours: 0,
            work_type: 'Testing'
        },
        {
            task_id: 'TASK-005',
            task_title: 'Update user interface for validation feedback',
            status: 'To Do',
            assignees: [{ first_name: 'Mike', last_name: 'Parker', initials: 'MP' }],
            estimated_hours: 10,
            actual_hours: 0,
            work_type: 'UI/UX'
        }
    ]
};

const UserStoryDetailsDummy = ({ onClose, storyId, onStoryDeleted }) => {
    const [value, setValue] = useState(0);
    const [storyDetails, setStoryDetails] = useState(dummyUserStoryDetails);
    const [statusAnchorEl, setStatusAnchorEl] = useState(null);
    const [priorityAnchorEl, setPriorityAnchorEl] = useState(null);
    const [updatedStoryDetails, setUpdatedStoryDetails] = useState({
        progress: dummyUserStoryDetails.progress,
        status: dummyUserStoryDetails.status,
        priority: dummyUserStoryDetails.priority,
        comment: '',
        actual_hours: dummyUserStoryDetails.actual_hours,
    });

    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [isEditStoryDetailsOpen, setIsEditStoryDetailsOpen] = useState(false);
    const [viewingTask, setViewingTask] = useState(false);
    const [selectedStory, setSelectedStory] = useState({});
    const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);

    const [userDetails, setUserDetails] = useState('');
    const [isManager, setIsManager] = useState(true); // Set to true for demo
    const [storyComments, setStoryComments] = useState(dummyUserStoryDetails.comments);
    const [isLoading, setIsLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('error');

    const [openEditModal, setOpenEditModal] = useState(false);
    const [actualTimeEdit, setActualTimeEdit] = useState({
        hours: '',
        minutes: ''
    });
    const [minutesError, setMinutesError] = useState(false);

    const [attachments, setAttachments] = useState([]);
    const [openPreview, setOpenPreview] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [storyApiMessage, setStoryApiMessage] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [storyToDelete, setStoryToDelete] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (updatedStoryDetails.status === 'Completed') {
            setUpdatedStoryDetails({
                ...updatedStoryDetails,
                progress: 100,
                status: 'Completed'
            });
        }
    }, [updatedStoryDetails.status]);

    useEffect(() => {
        const storedUserDetails = localStorage.getItem('userDetails');
        if (storedUserDetails) {
            try {
                const parsedUserDetails = JSON.parse(storedUserDetails);
                setUserDetails(parsedUserDetails);
                setIsManager(parsedUserDetails.user_type === 'Manager' || parsedUserDetails.user_type === 'Team Lead');
            } catch (error) {
                console.error('Error parsing userDetails from localStorage:', error);
                setIsManager(true); // Default to manager for demo
            }
        } else {
            setIsManager(true); // Default to manager for demo
        }
    }, []);

    const openStatusMenu = (event) => {
        setStatusAnchorEl(event.currentTarget);
    };

    const closeStatusMenu = () => {
        setStatusAnchorEl(null);
    };

    const openPriorityMenu = (event) => {
        setPriorityAnchorEl(event.currentTarget);
    };

    const closePriorityMenu = () => {
        setPriorityAnchorEl(null);
    };

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleTaskDetails = (taskId) => {
        setSelectedTaskId(taskId);
        setViewingTask(true);
    };

    const handleAddTask = () => {
        setAddTaskModalOpen(true);
    };

    const handleCloseAddTaskModal = () => {
        setAddTaskModalOpen(false);
    };

    const handleDeleteTask = (taskId) => {
        setStoryDetails(prev => ({
            ...prev,
            tasks: prev.tasks.filter(task => task.task_id !== taskId),
            total_tasks: prev.total_tasks - 1
        }));
        setSnackbarMessage('Task deleted successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    const handleBackFromTask = () => {
        setViewingTask(false);
        setSelectedTaskId(null);
    };

    const handleEditStoryDetails = (story) => {
        console.log('Editing story ID:', story);
        setIsEditStoryDetailsOpen(true);
        setSelectedStory(story);
    };

    const handleEditStoryPopupClose = () => {
        setIsEditStoryDetailsOpen(false);
    };

    function capitalizeFirstChar(str) {
        if (!str) {
            return "";
        }
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    const handleOpenEditModal = () => {
        const existingTime = updatedStoryDetails.actual_hours || storyDetails.actual_hours || '';
        if (existingTime) {
            const parts = String(existingTime).split('.');
            setActualTimeEdit({
                hours: parts[0] || '',
                minutes: parts[1] || ''
            });
        } else {
            setActualTimeEdit({ hours: '', minutes: '' });
        }
        setMinutesError(false);
        setOpenEditModal(true);
    };

    const handleCloseEditModal = () => {
        setOpenEditModal(false);
        setActualTimeEdit({ hours: '', minutes: '' });
        setMinutesError(false);
    };

    const handleActualTimeHoursChange = (e) => {
        const value = e.target.value;
        if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
            setActualTimeEdit(prev => ({ ...prev, hours: value }));
        }
    };

    const handleActualTimeMinutesChange = (e) => {
        const value = e.target.value;
        if (value === '' || (/^\d+$/.test(value))) {
            const numValue = parseInt(value);
            if (value === '' || numValue <= 59) {
                setActualTimeEdit(prev => ({ ...prev, minutes: value }));
                setMinutesError(false);
            } else {
                setMinutesError(true);
            }
        }
    };

    const handleSaveActualTime = () => {
        const { hours, minutes } = actualTimeEdit;

        if (!hours && !minutes) {
            handleCloseEditModal();
            return;
        }

        if (parseInt(minutes) > 59) {
            setMinutesError(true);
            return;
        }

        const minutesDecimal = minutes ? parseInt(minutes) / 60 : 0;
        const hoursValue = hours ? parseInt(hours) : 0;
        const formattedTime = hoursValue + minutesDecimal;

        setUpdatedStoryDetails(prev => ({
            ...prev,
            actual_hours: formattedTime
        }));

        handleCloseEditModal();
    };

    const handleSaveUpdateDetailsButton = async () => {
        setIsLoading(true);

        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Update story details locally
            setStoryDetails(prev => ({
                ...prev,
                progress: updatedStoryDetails.progress,
                status: updatedStoryDetails.status,
                priority: updatedStoryDetails.priority,
                actual_hours: updatedStoryDetails.actual_hours
            }));

            setStoryApiMessage('User Story Details are updated successfully!');
        } catch (error) {
            console.error('Update failed:', error);
            setStoryApiMessage('Failed to save the details. Please try again.');
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 1000);
        }
    };

    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files);

        const newAttachments = files.map(file => ({
            id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            file: file,
            name: file.name,
            type: file.type,
            url: URL.createObjectURL(file)
        }));

        setAttachments([...attachments, ...newAttachments]);
        event.target.value = null;
    };

    const handleOpenPreview = (attachment) => {
        setPreviewFile(attachment);
        setOpenPreview(true);
    };

    const handleClosePreview = () => {
        setOpenPreview(false);
        setPreviewFile(null);
    };

    const handleDeleteAttachment = (id) => {
        const updatedAttachments = attachments.filter(attachment => attachment.id !== id);
        setAttachments(updatedAttachments);
    };

    const handleDeleteClick = (storyId) => {
        setStoryToDelete(storyId);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!storyToDelete) return;

        try {
            // Simulate delete API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            setSnackbarOpen(true);
            setSnackbarMessage('Successfully Deleted the User Story');
            setSnackbarSeverity('success');

            setTimeout(() => {
                if (onStoryDeleted) {
                    onStoryDeleted();
                }
                if (onClose) {
                    onClose();
                }
            }, 1500);
        } catch (error) {
            console.error('Error in deleting user story', error);
            setSnackbarOpen(true);
            setSnackbarMessage('Error while deleting the User Story');
            setSnackbarSeverity('error');
        } finally {
            setDeleteConfirmOpen(false);
            setStoryToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmOpen(false);
        setStoryToDelete(null);
    };

    const UploadedFilesList = () => {
        const [openPreview, setOpenPreview] = useState(false);
        const [previewFile, setPreviewFile] = useState(null);

        const allFiles = [
            ...(storyDetails.attachments?.map(attachment => ({
                url: attachment.file_url,
                name: attachment.file_name,
                type: attachment.file_name?.match(/\.(jpeg|jpg|png|gif)$/i) ? 'image' :
                    attachment.file_name?.endsWith('.pdf') ? 'pdf' : 'file'
            })) || [])
        ];

        if (allFiles.length === 0) return null;

        const isImageFile = (filename) => {
            return filename?.match(/\.(jpeg|jpg|png|gif)$/i);
        };

        const handleOpenPreview = (file) => {
            setPreviewFile(file);
            if (isImageFile(file.name)) {
                setOpenPreview(true);
            } else {
                window.open(file.url, '_blank', 'noopener,noreferrer');
            }
        };

        const handleClosePreview = () => setOpenPreview(false);

        const handleDownload = async (file) => {
            try {
                if (!file.url) return;
                const link = document.createElement('a');
                link.href = file.url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                console.error('Download failed:', error);
                window.open(file.url, '_blank');
            }
        };

        return (
            <>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: 'rgba(100, 181, 246, 0.1)',
                        padding: '8px',
                        borderBottom: '1px solid #ccc',
                    }}
                >
                    <CloudUploadIcon color="primary" />
                    <Typography variant="subtitle2" color="primary" fontWeight={600}>
                        Uploaded files ({allFiles.length})
                    </Typography>
                </Box>

                <TableContainer component={Paper} elevation={2}>
                    <Table size="small">
                        <TableBody>
                            {allFiles.map((file, index) => (
                                <TableRow key={`${file.name}-${index}`}>
                                    <TableCell sx={{ width: 40 }}>
                                        {isImageFile(file.name) ? (
                                            <ImageIcon color="primary" />
                                        ) : file.name.endsWith('.pdf') ? (
                                            <PictureAsPdfIcon color="error" />
                                        ) : (
                                            <InsertDriveFileIcon color="action" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                            {file.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: 120 }}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Tooltip title="Preview">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenPreview(file)}
                                                    color="primary"
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Download">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDownload(file)}
                                                    color="primary"
                                                >
                                                    <CloudDownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {openPreview && previewFile && isImageFile(previewFile.name) && (
                    <Dialog
                        open={openPreview}
                        onClose={handleClosePreview}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>{previewFile.name}</DialogTitle>
                        <DialogContent sx={{ textAlign: 'center' }}>
                            <img
                                src={previewFile.url}
                                alt={previewFile.name}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    objectFit: 'contain'
                                }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                startIcon={<CloudDownloadIcon />}
                                onClick={() => handleDownload(previewFile)}
                                variant='contained'
                            >
                                Download
                            </Button>
                            <Button variant='outlined' onClick={handleClosePreview}>Close</Button>
                        </DialogActions>
                    </Dialog>
                )}
            </>
        );
    };

    const renderFileIcon = (fileType) => {
        if (fileType.startsWith('image/')) {
            return <ImageIcon />;
        } else if (fileType === 'application/pdf') {
            return <PictureAsPdfIcon />;
        } else {
            return <InsertDriveFileIcon />;
        }
    };

    const getWorkTypeColor = (workType) => {
        switch (workType) {
            case 'Development': return '#1976d2';
            case 'Enhancement': return '#9c27b0';
            case 'Bug': return '#d32f2f';
            case 'Feature': return '#4caf50';
            case 'Testing': return '#f57c00';
            case 'Documentation': return '#607d8b';
            case 'Analysis': return '#9c27b0';
            case 'UI/UX': return '#ff9800';
            default: return '#757575';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'To Do': return '#9e9e9e';
            case 'In Progress': return '#2196f3';
            case 'Completed': return '#4caf50';
            default: return '#757575';
        }
    };

    if (viewingTask && selectedTaskId) {
        // You would navigate to the task details here
        // For now, we'll just show a back button
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f0f4f8', borderRadius: '12px' }}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', p: 2 }}>
                    <img
                        src={Back}
                        alt="Back"
                        style={{
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            transition: 'transform 0.3s ease'
                        }}
                        onClick={handleBackFromTask}
                    />
                    <Typography variant="subtitle1" sx={{ ml: 2, fontWeight: 'medium' }}>
                        Back to User Story
                    </Typography>
                </Box>
                {/* Task Details Component would go here */}
                <Typography variant="h6" sx={{ p: 2 }}>
                    Task Details for Task ID: {selectedTaskId}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', bgcolor: '#f0f4f8', borderRadius: '12px' }}>
            <Box sx={{ flexGrow: 1, overflow: 'auto', borderRadius: '12px', p: 2 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '12px',
                    bgcolor: 'white'
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        p: 2,
                        borderRadius: '4px',
                        color: 'white',
                        justifyContent: 'space-between',
                        width: '100%'
                    }}>
                        <Typography variant="h5" component="span" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Description />
                            User Story Details
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', mt: 2, gap: 2 }}>
                    <Box sx={{ p: 3, bgcolor: '#ffffff', flexGrow: 1, display: 'flex' }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography
                                        variant="h6"
                                        color="primary"
                                        sx={{ fontWeight: 'bold', color: '#2e86de' }}
                                    >
                                        #{storyDetails.story_id}
                                    </Typography>
                                    <Typography variant="h6" sx={{ ml: 1 }}>
                                        {storyDetails.story_title}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    {isManager && (
                                        <Box>
                                            <IconButton aria-label="edit"
                                                size="small"
                                                sx={{ color: 'orange' }}
                                                onClick={() => handleEditStoryDetails(storyDetails)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton aria-label="delete" size="small" color="error"
                                                onClick={() => handleDeleteClick(storyDetails.story_id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" sx={{ color: '#666' }}>
                                    User Story •{' '}
                                    <Chip
                                        label={storyDetails.epic_name}
                                        size="small"
                                        sx={{
                                            backgroundColor: storyDetails.epic_color,
                                            color: 'white',
                                            fontSize: '0.75rem'
                                        }}
                                    />
                                </Typography>
                            </Box>
                            <Box mt={2} bgcolor="#f8fafc" p={2} borderRadius={1}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Business sx={{ color: '#1976d2' }} />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Epic
                                            </Typography>
                                            <Chip label={storyDetails?.epic_name || '-'} size="small" sx={{ backgroundColor: storyDetails.epic_color, color: 'white' }} />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PersonIcon sx={{ color: '#4caf50' }} />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Product Owner
                                            </Typography>
                                            <Chip label={capitalizeFirstChar(storyDetails?.product_owner) || 'N/A'} size="small" />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TimelineIcon sx={{ color: '#f57c00' }} />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Team Lead
                                            </Typography>
                                            <Chip label={capitalizeFirstChar(storyDetails?.team_lead) || '-'} size="small" />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CalendarTodayIcon sx={{ color: '#9c27b0' }} />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Timeline
                                            </Typography>
                                            <Chip
                                                label={storyDetails?.start_date ? new Date(storyDetails.start_date).toLocaleDateString() : '-'}
                                                size="small"
                                            />
                                            -
                                            <Chip
                                                label={storyDetails?.due_date ? new Date(storyDetails.due_date).toLocaleDateString() : '-'}
                                                size="small"
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AccessAlarm sx={{ color: '#800080' }} />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Estimated Time
                                            </Typography>
                                            <Chip label={formatTime(storyDetails?.estimated_hours) || '-'} size="small" />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AccessTimeFilled sx={{ color: '#2e7d32' }} />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Actual Time
                                            </Typography>
                                            <Chip
                                                label={formatTime(
                                                    updatedStoryDetails.actual_hours || storyDetails?.actual_hours
                                                )}
                                                size="small"
                                            />

                                            {!isManager && (
                                                <IconButton
                                                    aria-label="edit actual time"
                                                    size="small"
                                                    onClick={handleOpenEditModal}
                                                    sx={{ ml: 0.5 }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Box
                                sx={{
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    marginBottom: '16px',
                                    mt: 2,
                                    bgcolor: '#f8fafc'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        backgroundColor: 'rgba(100, 181, 246, 0.1)',
                                        padding: '8px',
                                        borderBottom: '1px solid #ccc',
                                    }}
                                >
                                    <DescriptionIcon color="primary" />
                                    <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                        Description
                                    </Typography>
                                </Box>
                                <Box sx={{ padding: '16px' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {capitalizeFirstChar(storyDetails?.description) || 'No Description is added'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    marginBottom: '16px',
                                    mt: 2,
                                    bgcolor: '#f8fafc'
                                }}
                            >
                                <UploadedFilesList />
                            </Box>

                            {/* Tasks Section */}
                            <Box sx={{
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                marginBottom: '16px',
                                bgcolor: '#f8fafc'
                            }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        backgroundColor: 'rgba(100, 181, 246, 0.1)',
                                        padding: '8px',
                                        borderBottom: '1px solid #ccc',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PlaylistAddCheckIcon color="primary" />
                                        <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                            Tasks ({storyDetails?.tasks?.length || 0})
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ({storyDetails.completed_tasks || 0} completed of {storyDetails.total_tasks || 0})
                                        </Typography>
                                    </Box>
                                    {isManager && (
                                        <IconButton
                                            size="small"
                                            onClick={handleAddTask}
                                            sx={{
                                                color: 'primary.main',
                                                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(25, 118, 210, 0.2)',
                                                }
                                            }}
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    )}
                                </Box>
                                <TableContainer>
                                    <Table aria-label="tasks">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Task Name</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Work Type</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Assignee</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Estimated Hours</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Actual Hours</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody sx={{ '& > tr': { borderBottom: '1px solid rgba(224, 224, 224, 1)' } }}>
                                            {storyDetails.tasks && storyDetails.tasks.length > 0 ? (
                                                storyDetails.tasks.map((task, index) => (
                                                    <TableRow
                                                        key={task.task_id}
                                                        sx={{
                                                            backgroundColor: index % 2 === 0 ? stripedRowColors.even : stripedRowColors.odd,
                                                            '&:last-child td, &:last-child th': { borderBottom: 0 },
                                                            cursor: 'pointer',
                                                            paddingX: 1,
                                                        }}
                                                    >
                                                        <TableCell
                                                            component="th"
                                                            scope="row"
                                                            onClick={() => handleTaskDetails(task.task_id)}
                                                        >
                                                            <Typography sx={{ fontWeight: 500 }}>
                                                                {task.task_title}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={task.work_type || 'Development'}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: getWorkTypeColor(task.work_type),
                                                                    color: 'white',
                                                                    fontSize: '0.7rem'
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={task.status}
                                                                sx={{
                                                                    backgroundColor: getStatusColor(task.status),
                                                                    color: 'white',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '0.7rem'
                                                                }}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {task.assignees && task.assignees.length > 0 ? (
                                                                task.assignees.map((assignee, idx) => (
                                                                    <Chip
                                                                        key={idx}
                                                                        label={assignee.initials || `${capitalizeFirstChar(assignee.first_name)} ${capitalizeFirstChar(assignee.last_name)}`}
                                                                        size="small"
                                                                        sx={{
                                                                            marginRight: 0.5,
                                                                            marginTop: 0.5,
                                                                            fontSize: '0.7rem'
                                                                        }}
                                                                    />
                                                                ))
                                                            ) : (
                                                                <Typography variant="caption" color="textSecondary">
                                                                    Not Assigned
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {formatTime(task.estimated_hours)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {formatTime(task.actual_hours)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                <Tooltip title="View">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleTaskDetails(task.task_id)}
                                                                    >
                                                                        <VisibilityIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                {isManager && (
                                                                    <Tooltip title="Delete">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={() => handleDeleteTask(task.task_id)}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={7} align="center">
                                                        <Typography variant="body2" color="textSecondary">
                                                            No tasks yet. Add one using the button above.
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>

                            <Box sx={{ mt: 4, mb: 2 }}>
                                <Tabs value={value} onChange={handleChange}>
                                    <Tab label={`${storyDetails?.comments?.length || 0} Comments`} />
                                    <Tab label="Activities" />
                                </Tabs>
                                <Box sx={{ mt: 2 }}>
                                    {value === 0 ? (
                                        <Box>
                                            <TextField
                                                fullWidth
                                                placeholder="Type a new comment here"
                                                variant="outlined"
                                                size="small"
                                                value={updatedStoryDetails.comment || ''}
                                                onChange={(e) => setUpdatedStoryDetails(prev => ({ ...prev, comment: e.target.value }))}
                                            />

                                            <Box sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                                                <Table size="small" aria-label="comments table">
                                                    <TableBody>
                                                        {storyDetails.comments && storyDetails.comments.map((comment) => (
                                                            <StripedTableRow key={comment.id}>
                                                                <TableCell sx={{ fontWeight: 'bold' }}>{capitalizeFirstChar(comment.comment)}</TableCell>
                                                                <TableCell sx={{ width: 50 }}>
                                                                    <Avatar sx={{
                                                                        width: 18, height: 18, fontSize: '0.75rem',
                                                                        backgroundColor: comment.user_type === 'Product Owner' ? '#9c27b0' : 
                                                                                         comment.user_type === 'Team Lead' ? '#2196f3' : '#4caf50',
                                                                        color: '#ffffff',
                                                                    }}
                                                                    >
                                                                        {comment.user_type === 'Product Owner' ? 'P' : 
                                                                         comment.user_type === 'Team Lead' ? 'T' : 'E'}
                                                                    </Avatar>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {capitalizeFirstChar(comment.user?.first_name)} {capitalizeFirstChar(comment.user?.last_name)}
                                                                    </Typography>
                                                                </TableCell>

                                                                <TableCell align="right" sx={{ width: 120 }}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {moment(comment.createdAt).format('YYYY-MM-DD HH:mm')}
                                                                    </Typography>
                                                                </TableCell>
                                                            </StripedTableRow>
                                                        ))}
                                                        {(!storyDetails.comments || storyDetails.comments.length === 0) && (
                                                            <TableRow>
                                                                <TableCell colSpan={4} align="center">No comments yet.</TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box sx={{ mt: 1 }}>
                                            {/* Activities content would go here */}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    {/* Right Sidebar */}
                    <Box sx={{ ml: 2, width: 400 }}>
                        <Paper sx={{ p: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2, mb: 2 }}>
                                <Chip
                                    label={updatedStoryDetails?.status || storyDetails?.status || 'Not Set'}
                                    color={statusColors[updatedStoryDetails?.status || storyDetails?.status] || 'default'}
                                    size="medium"
                                    variant="contained"
                                    sx={{
                                        padding: '4px',
                                        fontWeight: 'bold',
                                        color: statusTextColors[updatedStoryDetails?.status || storyDetails?.status] || statusTextColors.default
                                    }}
                                    onClick={isManager ? openStatusMenu : undefined}
                                />

                                <Menu
                                    anchorEl={statusAnchorEl}
                                    open={Boolean(statusAnchorEl)}
                                    onClose={() => setStatusAnchorEl(null)}
                                >
                                    <MenuItem onClick={() => {
                                        setUpdatedStoryDetails(prev => ({ ...prev, status: 'Ready for Dev' }));
                                        setStatusAnchorEl(null);
                                    }}>Ready for Dev</MenuItem>

                                    <MenuItem onClick={() => {
                                        setUpdatedStoryDetails(prev => ({ ...prev, status: 'In Development' }));
                                        setStatusAnchorEl(null);
                                    }}>In Development</MenuItem>

                                    <MenuItem onClick={() => {
                                        setUpdatedStoryDetails(prev => ({ ...prev, status: 'Completed' }));
                                        setStatusAnchorEl(null);
                                    }}>Completed</MenuItem>

                                    <MenuItem onClick={() => {
                                        setUpdatedStoryDetails(prev => ({ ...prev, status: 'On Hold' }));
                                        setStatusAnchorEl(null);
                                    }}>On Hold</MenuItem>
                                </Menu>
                                <Chip
                                    label={updatedStoryDetails?.priority || storyDetails?.priority || 'Not Set'}
                                    color={priorityColors[updatedStoryDetails?.priority || storyDetails?.priority] || 'default'}
                                    size="medium"
                                    variant="contained"
                                    sx={{ padding: '4px' }}
                                    onClick={isManager ? openPriorityMenu : undefined}
                                    icon={updatedStoryDetails?.priority === 'High' ? <PriorityHigh sx={{ fontSize: 14 }} /> : null}
                                >
                                    {updatedStoryDetails?.priority || storyDetails?.priority || 'Not Set'}
                                </Chip>
                                <Menu
                                    anchorEl={priorityAnchorEl}
                                    open={Boolean(priorityAnchorEl)}
                                    onClose={() => setPriorityAnchorEl(null)}
                                >
                                    <MenuItem onClick={() => {
                                        setUpdatedStoryDetails(prev => ({ ...prev, priority: 'High' }));
                                        setPriorityAnchorEl(null);
                                    }}>High</MenuItem>
                                    <MenuItem onClick={() => {
                                        setUpdatedStoryDetails(prev => ({ ...prev, priority: 'Medium' }));
                                        setPriorityAnchorEl(null);
                                    }}>Medium</MenuItem>
                                    <MenuItem onClick={() => {
                                        setUpdatedStoryDetails(prev => ({ ...prev, priority: 'Low' }));
                                        setPriorityAnchorEl(null);
                                    }}>Low</MenuItem>
                                </Menu>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, bgcolor: '#f8fafc', p: 2, borderRadius: 2 }}>
                                {isManager && (
                                    <TextField
                                        placeholder="0"
                                        fullWidth
                                        inputProps={{ min: 0, max: 100 }}
                                        variant="outlined"
                                        label="Progress Percentage"
                                        type="number"
                                        size="small"
                                        value={updatedStoryDetails.progress !== undefined ? updatedStoryDetails.progress : storyDetails.progress || ''}
                                        onChange={(e) => {
                                            const rawValue = e.target.value;
                                            setUpdatedStoryDetails(prev => ({ ...prev, progress: rawValue }));
                                        }}
                                        onBlur={(e) => {
                                            const value = e.target.value;
                                            if (value === '') {
                                                setUpdatedStoryDetails(prev => ({ ...prev, progress: '' }));
                                            } else {
                                                const parsedValue = parseInt(value, 10);
                                                if (isNaN(parsedValue) || parsedValue < 0 || parsedValue > 100) {
                                                    setUpdatedStoryDetails(prev => ({ ...prev, progress: prev.progress !== undefined ? prev.progress : storyDetails.progress || '' }));
                                                } else {
                                                    setUpdatedStoryDetails(prev => ({ ...prev, progress: parsedValue }));
                                                }
                                            }
                                        }}
                                    />
                                )}
                                <LinearProgress
                                    variant="determinate"
                                    value={parseInt(updatedStoryDetails.progress !== undefined ? updatedStoryDetails.progress : storyDetails.progress || 0)}
                                    sx={{ mt: 1, borderRadius: 4, height: 10 }}
                                />
                                <Box display="flex" justifyContent="space-between" mt={0.5}>
                                    <Typography variant="caption">0%</Typography>
                                    <Typography variant="caption">100%</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Tasks: {storyDetails.completed_tasks || 0}/{storyDetails.total_tasks || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {Math.round((storyDetails.completed_tasks || 0) / (storyDetails.total_tasks || 1) * 100)}% complete
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#666', mb: 1, mt: 2 }}>
                                ASSIGNED TO
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                                {storyDetails?.assignees?.map((assignee) => (
                                    <Box key={`${assignee.first_name}-${assignee.last_name}`} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar
                                            sx={{ width: 32, height: 32, bgcolor: assignee.color || '#b48ead' }}
                                        >
                                            {assignee.initials || `${assignee.first_name.charAt(0)}`.toUpperCase()}
                                        </Avatar>
                                        <Typography variant="body2" sx={{ ml: 2 }}>
                                            {`${capitalizeFirstChar(assignee.first_name)} ${capitalizeFirstChar(assignee.last_name)}`}
                                        </Typography>
                                    </Box>
                                ))}
                                {storyDetails?.assignees?.length === 0 && (
                                    <Typography variant="body2" color="textSecondary">
                                        Not Assigned
                                    </Typography>
                                )}
                            </Box>
                            {isManager && (
                                <Button
                                    variant="text"
                                    startIcon={<AddIcon />}
                                    sx={{ color: '#2e86de', textTransform: 'none' }}
                                >
                                    Add assigned
                                </Button>
                            )}

                            {/* Attachments section */}
                            {!isManager && (
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        Attachments
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<AttachFileIcon />}
                                            onClick={() => fileInputRef.current.click()}
                                            size="small"
                                        >
                                            Add Files
                                        </Button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            onChange={handleFileUpload}
                                            style={{ display: 'none' }}
                                        />
                                    </Box>

                                    {attachments.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Files to be uploaded:
                                            </Typography>
                                            <Grid container spacing={1} sx={{ mt: 1 }}>
                                                {attachments.map((attachment) => {
                                                    const lastDotIndex = attachment.name.lastIndexOf('.');
                                                    const fileName = lastDotIndex > 0 ? attachment.name.substring(0, lastDotIndex) : attachment.name;
                                                    const fileExtension = lastDotIndex > 0 ? attachment.name.substring(lastDotIndex) : '';

                                                    return (
                                                        <Grid item key={attachment.id} xs={12}>
                                                            <Paper
                                                                elevation={1}
                                                                sx={{
                                                                    p: 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    backgroundColor: 'background.paper',
                                                                    '&:hover': {
                                                                        bgcolor: 'action.hover',
                                                                    }
                                                                }}
                                                            >
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    minWidth: 0,
                                                                    flexGrow: 1,
                                                                    pr: 1
                                                                }}>
                                                                    {renderFileIcon(attachment.type)}
                                                                    <Box sx={{
                                                                        ml: 1,
                                                                        minWidth: 0,
                                                                        overflow: 'hidden'
                                                                    }}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            noWrap
                                                                            sx={{
                                                                                display: 'inline'
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                component="span"
                                                                                sx={{
                                                                                    display: 'inline-block',
                                                                                    maxWidth: 'calc(100% - 30px)',
                                                                                    textOverflow: 'ellipsis',
                                                                                    overflow: 'hidden',
                                                                                    whiteSpace: 'nowrap',
                                                                                    verticalAlign: 'bottom'
                                                                                }}
                                                                            >
                                                                                {fileName}
                                                                            </Box>
                                                                            <Box
                                                                                component="span"
                                                                                sx={{
                                                                                    color: 'text.secondary',
                                                                                    ml: 0.5,
                                                                                    flexShrink: 0
                                                                                }}
                                                                            >
                                                                                {fileExtension}
                                                                            </Box>
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteAttachment(attachment.id);
                                                                    }}
                                                                    sx={{
                                                                        color: 'text.secondary',
                                                                        '&:hover': {
                                                                            color: 'error.main',
                                                                            backgroundColor: 'transparent'
                                                                        }
                                                                    }}
                                                                >
                                                                    <CloseIcon fontSize="small" />
                                                                </IconButton>
                                                            </Paper>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </Box>
                                    )}
                                </Box>
                            )}
                            <Dialog
                                open={openPreview}
                                onClose={handleClosePreview}
                                maxWidth="md"
                                fullWidth
                            >
                                <DialogTitle>{previewFile?.name}</DialogTitle>
                                <DialogContent sx={{ position: 'relative', minHeight: '400px' }}>
                                    {previewFile && (
                                        <>
                                            <IconButton
                                                onClick={handleClosePreview}
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: 8,
                                                    color: 'grey.500',
                                                    bgcolor: 'background.paper',
                                                    zIndex: 1,
                                                    boxShadow: 1
                                                }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                            {previewFile.type.startsWith('image/') ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                                                    <img
                                                        src={previewFile.url}
                                                        alt={previewFile.name}
                                                        style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                                                    />
                                                </Box>
                                            ) : (
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: '100%',
                                                    textAlign: 'center'
                                                }}>
                                                    <Box sx={{ fontSize: 60, mb: 2 }}>
                                                        {renderFileIcon(previewFile.type)}
                                                    </Box>
                                                    <Typography variant="h6" gutterBottom>
                                                        {previewFile.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                        This file type cannot be previewed. Please download to view.
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        href={previewFile.url}
                                                        download={previewFile.name}
                                                        startIcon={<CloudDownloadIcon />}
                                                    >
                                                        Download File
                                                    </Button>
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </DialogContent>
                            </Dialog>

                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                                WATCHERS
                            </Typography>
                            {isManager && (
                                <Button
                                    variant="text"
                                    startIcon={<AddIcon />}
                                    sx={{ color: '#2e86de', textTransform: 'none' }}
                                >
                                    Add watchers
                                </Button>
                            )}
                            <Button
                                variant="text"
                                startIcon={<VisibilityIcon />}
                                sx={{ color: '#2e86de', textTransform: 'none', ml: 2 }}
                            >
                                Watch
                            </Button>
                        </Paper>
                        {storyApiMessage && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: storyApiMessage.includes('success') ? 'success.main' : 'error.main',
                                    fontWeight: 600
                                }}
                            >
                                {storyApiMessage}
                            </Typography>
                        )}
                        {isManager && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 2 }}>
                                <Button variant="contained" onClick={handleSaveUpdateDetailsButton}>
                                    Save
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Edit Actual Time Dialog */}
            <Dialog open={openEditModal} onClose={handleCloseEditModal} maxWidth="xs" fullWidth>
                <DialogTitle>Edit Actual Time</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'flex-start' }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Hours"
                            type="tel"
                            fullWidth
                            value={actualTimeEdit.hours}
                            onChange={handleActualTimeHoursChange}
                            inputProps={{ maxLength: 3 }}
                        />
                        <TextField
                            margin="dense"
                            label="Minutes"
                            type="tel"
                            fullWidth
                            value={actualTimeEdit.minutes}
                            onChange={handleActualTimeMinutesChange}
                            error={minutesError}
                            helperText={minutesError ? "Minutes should be 0-59" : ""}
                            inputProps={{ maxLength: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditModal}>Cancel</Button>
                    <Button onClick={handleSaveActualTime} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleCancelDelete}
                aria-labelledby="delete-confirmation-dialog"
            >
                <DialogTitle id="delete-confirmation-dialog">
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this user story? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

{ addTaskModalOpen && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 100,
                    }}
                >
                    <Box sx={{ maxWidth: '90%', maxHeight: '90%', overflow: 'auto' }}>
                        <CreateTask
                            onClose={handleCloseAddTaskModal}                        
                           
                            // projectId={taskDetails?.project_id} 
                        />
                    </Box>
                </Box>
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UserStoryDetailsDummy;